import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertCircle, CheckCircle, Clock } from "lucide-react";

interface ExamRequestFormProps {
  courseId: string;
  userEmail?: string;
  userName?: string;
  onSuccess?: () => void;
}

export function ExamRequestForm({ courseId, userEmail, userName, onSuccess }: ExamRequestFormProps) {
  const [showForm, setShowForm] = useState(false);
  const [examType, setExamType] = useState<"computer-based" | "paper-based">("computer-based");
  const [firstName, setFirstName] = useState(userName?.split(" ")[0] || "");
  const [lastName, setLastName] = useState(userName?.split(" ").slice(1).join(" ") || "");
  const [email, setEmail] = useState(userEmail || "");
  const [canAttendBaghdad, setCanAttendBaghdad] = useState(false);
  const [examDate, setExamDate] = useState("");
  const [examTime, setExamTime] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [existingRequest, setExistingRequest] = useState<any>(null);

  // Check for existing request on mount
  useEffect(() => {
    fetch(`/api/student/exam-requests/${courseId}`, {
      credentials: 'include'
    })
      .then(res => res.json())
      .then(data => {
        if (data.request) {
          setExistingRequest(data.request);
        }
      })
      .catch(() => {});
  }, [courseId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess(false);

    // Validation
    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      setError("Please fill in all required fields");
      return;
    }

    if (examType === "paper-based") {
      if (!canAttendBaghdad) {
        setError("You must confirm attendance in Baghdad for paper-based exam");
        return;
      }
      if (!examDate) {
        setError("Please select an exam date");
        return;
      }
      if (!examTime) {
        setError("Please select an exam time");
        return;
      }

      const selectedDate = new Date(examDate);
      const today = new Date();
      const daysInAdvance = Math.ceil(
        (selectedDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
      );

      if (daysInAdvance < 5) {
        setError("Exam date must be at least 5 days in advance");
        return;
      }
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('/api/student/exam-requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          courseId,
          examType,
          firstName,
          lastName,
          email,
          canAttendBaghdad: examType === "paper-based" ? canAttendBaghdad : undefined,
          examDate: examType === "paper-based" ? examDate : undefined,
          examTime: examType === "paper-based" ? examTime : undefined,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit exam request');
      }

      const data = await response.json();
      setExistingRequest(data.request);
      setSuccess(true);
      setShowForm(false);
      onSuccess?.();
    } catch (err: any) {
      setError(err.message || "Failed to submit exam request");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending": return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "approved": return "bg-green-100 text-green-800 border-green-200";
      case "rejected": return "bg-red-100 text-red-800 border-red-200";
      case "completed": return "bg-blue-100 text-blue-800 border-blue-200";
      default: return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  // Show existing request status
  if (existingRequest) {
    return (
      <Card className={`border-2 ${getStatusColor(existingRequest.status)}`}>
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-semibold text-lg mb-1">Exam Request Submitted</h3>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">Status:</span>
                  <span className={getStatusColor(existingRequest.status)}>
                    {existingRequest.status.charAt(0).toUpperCase() + existingRequest.status.slice(1)}
                  </span>
                </div>
                <p className="text-sm">
                  <strong>Exam Type:</strong> {existingRequest.examType.replace('-', ' ')}
                </p>
                {existingRequest.examCode && (
                  <p className="text-sm">
                    <strong>Exam Code:</strong> <span className="font-mono bg-white px-2 py-1 rounded">{existingRequest.examCode}</span>
                  </p>
                )}
                {existingRequest.notes && (
                  <p className="text-sm">
                    <strong>Admin Notes:</strong> {existingRequest.notes}
                  </p>
                )}
              </div>
            {existingRequest.status === 'rejected' && (
              <Button
                className="mt-3 w-full bg-[#8B0000] hover:bg-[#6B0000] text-white"
                onClick={() => {
                  setExistingRequest(null);
                  setShowForm(true);
                }}
              >
                Request Again
              </Button>
            )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show request button or form
  if (!showForm) {
    return (
      <Button
        size="lg"
        variant="outline"
        className="w-full gap-2 border-[#8B0000] text-[#8B0000] hover:bg-red-50"
        onClick={() => setShowForm(true)}
      >
        Request ITIL Certification Exam
      </Button>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Request ITIL Certification Exam</CardTitle>
        <CardDescription>
          Request to take the official ITIL certification exam through PeopleCert.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Exam Type Selection */}
          <div>
            <Label className="text-base font-semibold mb-3 block">Exam Type</Label>
            <div className="space-y-3">
              <div
                className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  examType === "computer-based"
                    ? "border-[#8B0000] bg-red-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
                onClick={() => setExamType("computer-based")}
              >
                <div className="flex items-center gap-3">
                  <input
                    type="radio"
                    name="examType"
                    value="computer-based"
                    checked={examType === "computer-based"}
                    onChange={(e) => setExamType(e.target.value as "computer-based")}
                    className="w-4 h-4"
                  />
                  <div>
                    <p className="font-semibold text-gray-800">Computer-Based Exam</p>
                    <p className="text-sm text-gray-500">
                      Take the exam online through PeopleCert website. You will receive an exam code.
                    </p>
                  </div>
                </div>
              </div>

              <div
                className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  examType === "paper-based"
                    ? "border-[#8B0000] bg-red-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
                onClick={() => setExamType("paper-based")}
              >
                <div className="flex items-center gap-3">
                  <input
                    type="radio"
                    name="examType"
                    value="paper-based"
                    checked={examType === "paper-based"}
                    onChange={(e) => setExamType(e.target.value as "paper-based")}
                    className="w-4 h-4"
                  />
                  <div>
                    <p className="font-semibold text-gray-800">Paper-Based Exam</p>
                    <p className="text-sm text-gray-500">
                      Take the exam on paper in Baghdad, Iraq. Available times: 12:00-17:00 (Fri-Sat)
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Personal Information */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800">Personal Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="firstName">First Name *</Label>
                <Input
                  id="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="John"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="lastName">Last Name *</Label>
                <Input
                  id="lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Doe"
                  className="mt-1"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="john@example.com"
                className="mt-1"
              />
            </div>
          </div>

          {/* Paper-Based Specific Fields */}
          {examType === "paper-based" && (
            <div className="space-y-4 p-4 bg-red-50 rounded-lg border border-red-200">
              <h3 className="font-semibold text-gray-800">Paper-Based Exam Details</h3>
              
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="canAttendBaghdad"
                  checked={canAttendBaghdad}
                  onChange={(e) => setCanAttendBaghdad(e.target.checked)}
                  className="mt-1"
                />
                <Label htmlFor="canAttendBaghdad" className="text-sm cursor-pointer">
                  I confirm that I can attend the exam in Baghdad, Iraq
                </Label>
              </div>

              <div>
                <Label htmlFor="examDate">Exam Date * (at least 5 days in advance)</Label>
                <Input
                  id="examDate"
                  type="date"
                  value={examDate}
                  onChange={(e) => setExamDate(e.target.value)}
                  className="mt-1"
                  min={
                    new Date(Date.now() + 5 * 24 * 60 * 60 * 1000)
                      .toISOString()
                      .split("T")[0]
                  }
                />
              </div>

              <div>
                <Label htmlFor="examTime">Exam Time *</Label>
                <select
                  id="examTime"
                  value={examTime}
                  onChange={(e) => setExamTime(e.target.value)}
                  className="w-full mt-1 p-2 border rounded-lg"
                >
                  <option value="">Select exam time</option>
                  {examDate && (
                    <>
                      {new Date(examDate).getDay() >= 0 && new Date(examDate).getDay() <= 6 && (
                        <option value="15:00-17:00">15:00 - 17:00 (3 PM - 5 PM)</option>
                      )}
                      {(new Date(examDate).getDay() === 5 || new Date(examDate).getDay() === 6) && (
                        <option value="12:00-17:00">12:00 - 17:00 (12 PM - 5 PM)</option>
                      )}
                    </>
                  )}
                </select>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-green-600">Exam request submitted successfully!</p>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowForm(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-[#8B0000] hover:bg-[#6B0000] text-white"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit Exam Request"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
